import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon, CircleStackIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import { clearDwRefreshError } from "../../../store/slices/SupportDebugSlice";
import { triggerSupportDebugDwRefreshAsync } from "../../../store/thunks/SupportDebugThunk";
import { DbtRefreshCommand } from "../../../services/SupportDebugService";
import { useToast } from "../../../contexts/ToastContext";
import { Button } from "../../Catalyst/button";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "../../Catalyst/dialog";

// The only command exposed from the portal today. Rebuilds the DW tables
// holding user profile, connected accounts, etc.
const REFRESH_COMMAND: DbtRefreshCommand = "refresh-user-profile";

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  month: "short",
  day: "numeric",
});

// ISO timestamp -> "Sep 16, 3:42 PM" (viewer's local time).
const formatTriggeredAt = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : timeFormatter.format(date);
};

// "Refresh DW tables" button for the Product Support header plus its
// confirmation dialog. The refresh is page-global (not per user) and kicks
// off a dbt run, so it always asks before firing and disables itself while a
// run is in flight.
const DwRefreshAction = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccessToast } = useToast();
  const { status, error, lastTriggeredAt } = useSelector(
    (state: RootState) => state.SupportDebugStore.dwRefresh
  );

  const [open, setOpen] = useState(false);
  const isRefreshing = status === STATUS.LOADING;

  const handleOpen = () => {
    dispatch(clearDwRefreshError());
    setOpen(true);
  };

  const handleClose = () => {
    if (isRefreshing) return; // keep the dialog up until the request settles
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (isRefreshing) return;
    const result = await dispatch(triggerSupportDebugDwRefreshAsync({ command: REFRESH_COMMAND }));
    if (!triggerSupportDebugDwRefreshAsync.fulfilled.match(result)) {
      // Error is rendered inline; keep the dialog open so the admin can retry.
      return;
    }
    const message = result.payload.response?.msg ?? result.payload.response?.message;
    showSuccessToast(
      "Refresh triggered",
      message || "The user profile data-warehouse tables are being refreshed. This can take a few minutes."
    );
    setOpen(false);
  };

  return (
    <>
      <div className="flex flex-col items-end gap-1">
        <Button outline onClick={handleOpen} disabled={isRefreshing} title="Refresh the data-warehouse tables">
          <ArrowPathIcon data-slot="icon" className={isRefreshing ? "animate-spin" : undefined} />
          {isRefreshing ? "Refreshing..." : "Refresh DW tables"}
        </Button>
        {lastTriggeredAt && (
          <span className="text-xs text-textColor3">
            Last triggered {formatTriggeredAt(lastTriggeredAt)}
          </span>
        )}
      </div>

      <Dialog open={open} onClose={handleClose} size="md">
        <div className="flex items-start justify-between gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-shadowborder bg-white shadow-light-sm">
            <CircleStackIcon className="h-6 w-6 text-texthead" aria-hidden="true" />
          </span>
          <button
            type="button"
            onClick={handleClose}
            disabled={isRefreshing}
            aria-label="Close"
            className="shrink-0 rounded-md p-1 text-zinc-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <DialogTitle className="mt-4">Refresh data-warehouse tables?</DialogTitle>
        <DialogDescription>
          This runs the on-demand <span className="font-mono text-xs">{REFRESH_COMMAND}</span> job,
          which rebuilds the data-warehouse tables holding user profile, connected accounts and
          related data for all users. It is not limited to the selected user and can take a few
          minutes to complete.
        </DialogDescription>
        {error && <p className="mt-4 text-sm font-medium text-red-500">{error}</p>}
        <DialogActions>
          <Button outline onClick={handleClose} disabled={isRefreshing}>
            Cancel
          </Button>
          <Button color="dark" onClick={handleConfirm} disabled={isRefreshing}>
            <ArrowPathIcon data-slot="icon" className={isRefreshing ? "animate-spin" : undefined} />
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DwRefreshAction;
